import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@mui/material";
import { getMandateSetupStatus } from "../../AutopayMandate/autopayMandateApi";
import { getExpiredPopupCopy, FLAG_REASON } from "./flagReasons";
import { refreshSubscriptionFlag } from "./subscriptionFlagStore";
import { formatDate } from "../subscriptionFormat";
import "../Subscription.css";

// ================================================================
// Subscription expired popup (backend guide S0 "FlagPopup").
//
// Where : rendered only by SubscriptionGuard, on any protected screen,
//         while subscription-flag says SubscriptionExpired = true.
// Text  : from flagReasons.getExpiredPopupCopy(flag.Reason) —
//         EXPIRED_AUTOPAY_DEBIT_FAILED → "AutoPay debit failed…" + Pay Now
//         EXPIRED_AUTOPAY_OFF          → "AutoPay is not active…"
//                                        + Set AutoPay (+ Pay Now only if a
//                                        mandate exists — checked through
//                                        GET UPIAPI/mandate-setup-status)
//         Due amount = flag.OutstandingAmount, overdue days/since below.
// Buttons:
//   Pay Now        → /subscription-billing (ledger tab) where the
//                    per-month Pay Now lives.
//   Set AutoPay    → /autopay-mandate.
//   Check again    → re-calls subscription-flag (the flag clears by
//                    itself once the bank callback lands).
// No close button on purpose: the guide says the frontend must restrict
// the service until dues are cleared. The RM can always leave through the
// buttons above, because those screens are exempt from the guard.
// ================================================================
const SubscriptionFlagPopup = ({ open, flag }) => {
  const navigate = useNavigate();
  const [hasMandate, setHasMandate] = useState(flag?.AutopayOn ?? false);
  const [checking, setChecking] = useState(false);

  const { title, text, showSetAutoPay } = getExpiredPopupCopy(flag);

  // EXPIRED_AUTOPAY_OFF: Pay Now debits through the mandate, so offer it
  // only when one exists (guide S0: "Pay Now (if a mandate exists)").
  useEffect(() => {
    if (!open) return;
    if (flag?.Reason !== FLAG_REASON.AUTOPAY_OFF) {
      setHasMandate(true);
      return;
    }
    let cancelled = false;
    getMandateSetupStatus({ employeeId: localStorage.getItem("ID") }).then((res) => {
      if (!cancelled) setHasMandate(Boolean(res?.data?.hasAnyMandate || res?.data?.isActive));
    });
    return () => {
      cancelled = true;
    };
  }, [open, flag?.Reason]);

  const goToPayNow = () =>
    navigate("/subscription-billing", { state: { tab: "subscriptionFeeLedger" } });

  const goToAutoPay = () => navigate("/autopay-mandate");

  const handleCheckAgain = async () => {
    setChecking(true);
    await refreshSubscriptionFlag();
    setChecking(false);
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <div className="sub-flag-popup">
        <div className="sub-flag-popup__icon">
          <i className="bx bx-error-circle" />
        </div>
        <h5 className="sub-flag-popup__title">{title}</h5>
        <p className="sub-flag-popup__text">{text}</p>

        {flag?.OverdueDays > 0 && (
          <p className="sub-flag-popup__meta">
            Overdue for {flag.OverdueDays} day{flag.OverdueDays > 1 ? "s" : ""}
            {flag.OverdueSince ? ` (since ${formatDate(flag.OverdueSince)})` : ""}
          </p>
        )}

        <div className="sub-flag-popup__actions">
          {hasMandate && (
            <button type="button" className="sub-btn sub-btn--primary" onClick={goToPayNow}>
              Pay Now
            </button>
          )}
          {showSetAutoPay && (
            <button type="button" className="sub-btn sub-btn--gold" onClick={goToAutoPay}>
              Set AutoPay
            </button>
          )}
        </div>

        <button
          type="button"
          className="sub-btn sub-btn--link"
          onClick={handleCheckAgain}
          disabled={checking}
        >
          {checking ? "Checking…" : "Already paid? Check again"}
        </button>
      </div>
    </Dialog>
  );
};

export default SubscriptionFlagPopup;
